import { Button, Intent, Menu, MenuItem, Popover } from "@snpjs/core";
import { useField } from "@ekz/formix";
import { useState } from "react";
import {
  DatePickerField,
  defineField,
  useFormWithFields,
  withForm,
} from "@snpjs/form";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import { AdminViewApi } from "../../api/AdminViewApi";
import { Table2 } from "../../components/table2/Table2";
import { useRequest } from "../../hooks/useRequest";
import type { IEmailLogEntry } from "../../shared/model/IEmailLogEntry";
import { exportTableData } from "./utils/exportTableData";
import sectionStyles from "./AdminViewSection.module.scss";
import styles from "./EmailLogsSection.module.scss";

const createEmailLogFields = () => ({
  startDate: defineField<Date | null>(null),
  endDate: defineField<Date | null>(null),
});

const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

const getRecipients = (row: Record<string, unknown>) => {
  const recipients = row.EMAIL_RECIPIENT;
  if (!Array.isArray(recipients)) return [];

  return recipients.filter(
    (recipient): recipient is Record<string, unknown> =>
      recipient !== null && typeof recipient === "object"
  );
};

const matchesRecipient = (row: Record<string, unknown>, searchValue: string) => {
  const query = searchValue.trim().toLowerCase();
  if (!query) return true;

  return getRecipients(row).some((recipient) =>
    String(recipient.RECIPIENT ?? "").toLowerCase().includes(query)
  );
};

const getExportRows = (
  rows: Record<string, unknown>[]
): Record<string, unknown>[] =>
  rows.map((row) => {
    const recipients = getRecipients(row);
    const { EMAIL_RECIPIENT: _emailRecipients, ...emailLog } = row;

    return {
      ...emailLog,
      RECIPIENT: recipients
        .map((recipient) => String(recipient.RECIPIENT ?? ""))
        .filter(Boolean)
        .join("; "),
      RECIPIENT_TYPE: recipients
        .map((recipient) => String(recipient.RECIPIENT_TYPE ?? ""))
        .filter(Boolean)
        .join("; "),
    };
  });

const EmailLogsFields: React.FC = () => {
  const { fields } = useFormWithFields(createEmailLogFields, []);
  const startDate = useField(fields.startDate);
  const endDate = useField(fields.endDate);
  const request = useRequest();
  const [emailLogs, setEmailLogs] = useState<IEmailLogEntry[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);

  const exportData = (
    format: "csv" | "excel",
    rows: Record<string, unknown>[] = emailLogs
  ) => {
    if (rows.length === 0) return;

    const exportRows = getExportRows(rows);
    exportTableData(exportRows, format, "email_logs", "EmailLogs");
  };

  const exportMenu = (table?: any) => (
    <Menu>
      <MenuItem
        icon={<FileCsv />}
        text="CSV"
        onClick={() =>
          exportData(
            "csv",
            table
              ? table.getFilteredRowModel().rows.map((row: any) => row.original)
              : emailLogs
          )
        }
      />
      <MenuItem
        icon={<MicrosoftExcelLogo />}
        text="Excel"
        onClick={() =>
          exportData(
            "excel",
            table
              ? table.getFilteredRowModel().rows.map((row: any) => row.original)
              : emailLogs
          )
        }
      />
    </Menu>
  );

  const fetchEmailLogs = async () => {
    const selectedStartDate = startDate.value;
    if (selectedStartDate == null) return;

    setIsLoading(true);
    await request.send(async () => {
      const data = await AdminViewApi.getEmailLogInfo(
        formatDateForApi(selectedStartDate),
        endDate.value == null ? undefined : formatDateForApi(endDate.value)
      );
      setEmailLogs(data);
      setHasLoaded(true);
    });
    setIsLoading(false);
  };

  return (
    <div className={styles.emailLogs}>
      <div className={styles.controls}>
        <div className={styles.dateField}>
          <DatePickerField
            className={styles.datePicker}
            field={fields.startDate}
            label="Start Date"
            required
            placeholder="YYYY-MM-DD"
            popoverProps={{
              placement: "bottom-start",
              popoverClassName: styles.datePickerPopover,
            }}
          />
        </div>
        <span className={styles.arrow} aria-hidden="true">
          →
        </span>
        <div className={styles.dateField}>
          <DatePickerField
            className={styles.datePicker}
            field={fields.endDate}
            label="End Date"
            placeholder="YYYY-MM-DD"
            popoverProps={{
              placement: "bottom-start",
              popoverClassName: styles.datePickerPopover,
            }}
          />
        </div>
        <Button
          intent="primary"
          className={styles.runButton}
          text="Run"
          disabled={startDate.value == null || isLoading}
          loading={isLoading}
          onClick={() => void fetchEmailLogs()}
        />
      </div>
      {hasLoaded && (
        <>
          <div className={sectionStyles.countHeader}>{visibleCount} Records Found</div>
          <div className={styles.tableContainer}>
            <Table2
              data={emailLogs}
              isLoading={isLoading}
              noResultsTitle="No email logs found"
              onVisibleCountChange={setVisibleCount}
              filterTypes={{
                CREATION_DATE: "date",
                CREATION_TIME: "time",
                BODY: "text",
              }}
              hiddenColumns={["EMAIL_RECIPIENT"]}
              globalFilterFn={matchesRecipient}
              autoHeight
              canExpandRow={(row) => getRecipients(row).length > 0}
              getSubComponent={(row) => (
                <div className={styles.recipientTable}>
                  <Table2
                    data={getRecipients(row)}
                    noResultsTitle="No recipients found"
                    showUtilityToolbar={false}
                    enableGlobalFilter={false}
                    displayHeader={false}
                    autoHeight
                  />
                </div>
              )}
              showUtilityToolbar={false}
              leftToolbar={(table) => (
                <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                  <Button
                    intent={Intent.PRIMARY}
                    icon={<Download />}
                    rightIcon="caret-down"
                    text="Export"
                    fill
                    alignText="left"
                  />
                </Popover>
              )}
              rightToolbar={
                <span className={styles.recipientSearchLabel}>
                  Search by recipient:
                </span>
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export const EmailLogsSection = withForm(EmailLogsFields);
